import { onMounted, onUnmounted } from 'vue'
import { reportActiveTimeApi } from '@/api/active-time.api'
import { useUserStore } from '@/store/user.store'

const REPORT_INTERVAL = 30 * 1000 // 30 秒上报一次
const STORAGE_PREFIX = 'active_time_seconds_'

function getToday(): string {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function getStoredSeconds(date: string): number {
  const raw = localStorage.getItem(`${STORAGE_PREFIX}${date}`)
  if (!raw) return 0
  const n = parseInt(raw, 10)
  return isNaN(n) ? 0 : n
}

function setStoredSeconds(date: string, seconds: number) {
  localStorage.setItem(`${STORAGE_PREFIX}${date}`, String(seconds))
}

export function useActiveTimeReport() {
  const userStore = useUserStore()
  let tickTimer: ReturnType<typeof setInterval> | null = null
  let reportTimer: ReturnType<typeof setInterval> | null = null
  let accumulated = 0
  let stopped = false // 停报标记：服务端返回 continueReport=false 时置 true
  const date = getToday()
  const uid = userStore.userInfo?.uid || 'uid'
  const clientDayId = `${uid}-pcweb-${date.replace(/-/g, '')}`

  /** 停止当天所有计时与上报 */
  function stopAll() {
    stopped = true
    if (tickTimer) {
      clearInterval(tickTimer)
      tickTimer = null
    }
    if (reportTimer) {
      clearInterval(reportTimer)
      reportTimer = null
    }
    console.log('[停留上报] 收到停报信号，当天不再上报')
  }

  async function report(isFinal = false) {
    const token = localStorage.getItem('token')
    if (!token || stopped) return

    try {
      const res = await reportActiveTimeApi({
        date,
        clientDayId,
        clientAccumulatedSeconds: accumulated
      })
      // 以服务端返回的累计秒数为基准，避免切换页面/接口时本地计数落后
      const serverSeconds = res.data?.activeSeconds ?? accumulated
      accumulated = Math.max(accumulated, serverSeconds)
      setStoredSeconds(date, accumulated)
      console.log(isFinal ? '停留上报（关闭补传）' : '停留上报', res.data)

      // 停报信号：服务端返回 continueReport=false 时停止当天计时与上报
      if (res.data?.continueReport === false) {
        stopAll()
      }
    } catch (error) {
      console.error('停留上报失败', error)
    }
  }

  function reportSync() {
    const token = localStorage.getItem('token')
    if (!token || stopped) return

    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/study/active-time/report', false)
      xhr.setRequestHeader('Content-Type', 'application/json')
      xhr.setRequestHeader('token', token)
      xhr.send(
        JSON.stringify({
          date,
          clientDayId,
          clientAccumulatedSeconds: accumulated
        })
      )
      // 同步请求可直接读取服务端返回的累计秒数，用于页面切换后本地计数校准
      if (xhr.status === 200) {
        const res = JSON.parse(xhr.responseText)
        const serverSeconds = res?.data?.activeSeconds ?? accumulated
        accumulated = Math.max(accumulated, serverSeconds)
        // 同步补传也检查停报信号
        if (res?.data?.continueReport === false) {
          stopAll()
        }
      }
      setStoredSeconds(date, accumulated)
    } catch (error) {
      console.error('停留同步上报失败', error)
    }
  }

  function tick() {
    if (!stopped) {
      accumulated += 1
    }
  }

  onMounted(() => {
    const token = localStorage.getItem('token')
    if (!token) return

    // 读取本地当日已累积秒数，作为初始值
    const stored = getStoredSeconds(date)
    accumulated = stored

    // 每秒累加
    tickTimer = setInterval(tick, 1000)

    // 每 30 秒上报一次
    reportTimer = setInterval(() => {
      report()
    }, REPORT_INTERVAL)

    // 页面关闭前补传
    const handleBeforeUnload = () => {
      reportSync()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)

    onUnmounted(() => {
      if (tickTimer) clearInterval(tickTimer)
      if (reportTimer) clearInterval(reportTimer)
      window.removeEventListener('beforeunload', handleBeforeUnload)
    })
  })
}
